import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Search, 
  Plus, 
  Trash2, 
  MapPin, 
  Calendar,
  Edit2,
  Users,
  DollarSign
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  collection, 
  getDocs, 
  doc, 
  deleteDoc, 
  addDoc, 
  updateDoc,
  query, 
  orderBy 
} from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface Trip {
  id: string;
  title: string;
  description: string;
  destination: string;
  price: number;
  duration: string;
  maxParticipants: number;
  currentParticipants: number;
  startDate: Date;
  endDate: Date;
  createdAt: Date;
  isActive: boolean;
  organizer: string;
}

export function AdminTripManagement() {
  const { t, dir } = useLanguage();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [filteredTrips, setFilteredTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tripToDelete, setTripToDelete] = useState<Trip | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTrip, setEditingTrip] = useState<Trip | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [newTrip, setNewTrip] = useState({
    title: '',
    description: '',
    destination: '',
    price: 0,
    duration: '',
    maxParticipants: 0,
    startDate: '',
    endDate: '',
    organizer: 'Admin',
    isActive: true
  });

  const fetchTrips = async () => {
    try {
      setLoading(true);
      
      const tripsRef = collection(db, 'trips');
      const tripsQuery = query(tripsRef, orderBy('createdAt', 'desc'));
      const tripsSnapshot = await getDocs(tripsQuery);

      const tripsData = tripsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          startDate: data.startDate?.toDate() || new Date(),
          endDate: data.endDate?.toDate() || new Date(),
          currentParticipants: data.participants?.length || 0
        } as Trip;
      });

      setTrips(tripsData);
      setFilteredTrips(tripsData);
    } catch (error) {
      console.error('Error fetching trips:', error);
      toast.error(t('dashboard.admin.trips.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTrips(trips);
    } else {
      const filtered = trips.filter(trip =>
        trip.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trip.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trip.destination.toLowerCase().includes(searchQuery.toLowerCase()) ||
        trip.organizer.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredTrips(filtered);
    }
  }, [searchQuery, trips]);

  const handleDeleteClick = (trip: Trip) => {
    setTripToDelete(trip);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!tripToDelete) return;

    try {
      setDeleting(true);
      await deleteDoc(doc(db, 'trips', tripToDelete.id));
      
      setTrips(prev => prev.filter(t => t.id !== tripToDelete.id));
      toast.success(t('dashboard.admin.trips.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error(t('dashboard.admin.trips.messages.deleteError'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setTripToDelete(null);
    }
  };

  const handleCreateTrip = async () => {
    if (!newTrip.title.trim() || !newTrip.destination.trim()) {
      toast.error('Title and destination are required');
      return;
    }

    try {
      setCreating(true);
      const tripData = {
        ...newTrip,
        createdAt: new Date(),
        startDate: new Date(newTrip.startDate),
        endDate: new Date(newTrip.endDate),
        participants: [],
        currentParticipants: 0
      };

      const docRef = await addDoc(collection(db, 'trips'), tripData);
      
      const newTripWithId = {
        id: docRef.id,
        ...tripData,
        currentParticipants: 0
      };

      setTrips(prev => [newTripWithId, ...prev]);
      setNewTrip({
        title: '',
        description: '',
        destination: '',
        price: 0,
        duration: '',
        maxParticipants: 0,
        startDate: '',
        endDate: '',
        organizer: 'Admin',
        isActive: true
      });
      setCreateDialogOpen(false);
      toast.success(t('dashboard.admin.trips.messages.createSuccess'));
    } catch (error) {
      console.error('Error creating trip:', error);
      toast.error(t('dashboard.admin.trips.messages.createError'));
    } finally {
      setCreating(false);
    }
  };

  const handleEditClick = (trip: Trip) => {
    setEditingTrip(trip);
    setNewTrip({
      title: trip.title,
      description: trip.description,
      destination: trip.destination,
      price: trip.price,
      duration: trip.duration,
      maxParticipants: trip.maxParticipants,
      startDate: trip.startDate.toISOString().split('T')[0],
      endDate: trip.endDate.toISOString().split('T')[0],
      organizer: trip.organizer,
      isActive: trip.isActive
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTrip = async () => {
    if (!editingTrip || !newTrip.title.trim() || !newTrip.destination.trim()) {
      toast.error('Title and destination are required');
      return;
    }

    try {
      setUpdating(true);
      await updateDoc(doc(db, 'trips', editingTrip.id), {
        title: newTrip.title,
        description: newTrip.description,
        destination: newTrip.destination,
        price: newTrip.price,
        duration: newTrip.duration,
        maxParticipants: newTrip.maxParticipants,
        startDate: new Date(newTrip.startDate),
        endDate: new Date(newTrip.endDate),
        organizer: newTrip.organizer,
        isActive: newTrip.isActive
      });

      setTrips(prev => prev.map(t => 
        t.id === editingTrip.id 
          ? { 
              ...t, 
              ...newTrip,
              startDate: new Date(newTrip.startDate),
              endDate: new Date(newTrip.endDate)
            }
          : t
      ));

      setEditDialogOpen(false);
      setEditingTrip(null);
      setNewTrip({
        title: '',
        description: '',
        destination: '',
        price: 0,
        duration: '',
        maxParticipants: 0,
        startDate: '',
        endDate: '',
        organizer: 'Admin',
        isActive: true
      });
      toast.success(t('dashboard.admin.trips.messages.updateSuccess'));
    } catch (error) {
      console.error('Error updating trip:', error);
      toast.error(t('dashboard.admin.trips.messages.updateError'));
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('dashboard.admin.trips.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={dir}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">
            {t('dashboard.admin.trips.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('dashboard.admin.trips.pageDescription')}
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4`} />
              {t('dashboard.admin.trips.createTrip')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('dashboard.admin.trips.createTrip')}</DialogTitle>
              <DialogDescription>
                {t('dashboard.admin.trips.createTripDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">{t('dashboard.admin.trips.tripTitle')}</Label>
                <Input
                  id="title"
                  value={newTrip.title}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, title: e.target.value }))}
                  placeholder={t('dashboard.admin.trips.titlePlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="destination">{t('dashboard.admin.trips.destination')}</Label>
                <Input
                  id="destination"
                  value={newTrip.destination}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, destination: e.target.value }))}
                  placeholder={t('dashboard.admin.trips.destinationPlaceholder')}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">{t('dashboard.admin.trips.description')}</Label>
                <Textarea
                  id="description"
                  value={newTrip.description}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('dashboard.admin.trips.descriptionPlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="price">{t('dashboard.admin.trips.price')}</Label>
                <Input
                  id="price"
                  type="number"
                  value={newTrip.price}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, price: Number(e.target.value) }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="duration">{t('dashboard.admin.trips.duration')}</Label>
                <Input
                  id="duration"
                  value={newTrip.duration}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, duration: e.target.value }))}
                  placeholder={t('dashboard.admin.trips.durationPlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="maxParticipants">{t('dashboard.admin.trips.maxParticipants')}</Label>
                <Input
                  id="maxParticipants"
                  type="number"
                  value={newTrip.maxParticipants}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, maxParticipants: Number(e.target.value) }))}
                  placeholder="0"
                />
              </div>
              <div>
                <Label htmlFor="organizer">{t('dashboard.admin.trips.organizer')}</Label>
                <Input
                  id="organizer"
                  value={newTrip.organizer}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, organizer: e.target.value }))}
                  placeholder={t('dashboard.admin.trips.organizerPlaceholder')}
                />
              </div>
              <div>
                <Label htmlFor="startDate">{t('dashboard.admin.trips.startDate')}</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={newTrip.startDate}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="endDate">{t('dashboard.admin.trips.endDate')}</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={newTrip.endDate}
                  onChange={(e) => setNewTrip(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                {t('dashboard.admin.trips.cancel')}
              </Button>
              <Button onClick={handleCreateTrip} disabled={creating}>
                {creating ? t('dashboard.admin.trips.creating') : t('dashboard.admin.trips.create')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t('dashboard.admin.trips.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredTrips.length} {t('dashboard.admin.trips.of')} {trips.length} {t('dashboard.admin.trips.tripsCount')}
        </div>
      </div>

      {/* Trips Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t('dashboard.admin.trips.allTrips')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTrips.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? t('dashboard.admin.trips.noTripsFound') : t('dashboard.admin.trips.noTrips')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('dashboard.admin.trips.tableHeaders.trip')}</TableHead>
                    <TableHead>{t('dashboard.admin.trips.tableHeaders.destination')}</TableHead>
                    <TableHead>{t('dashboard.admin.trips.tableHeaders.price')}</TableHead>
                    <TableHead>{t('dashboard.admin.trips.tableHeaders.participants')}</TableHead>
                    <TableHead>{t('dashboard.admin.trips.tableHeaders.dates')}</TableHead>
                    <TableHead>{t('dashboard.admin.trips.tableHeaders.status')}</TableHead>
                    <TableHead className="text-right">{t('dashboard.admin.trips.tableHeaders.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTrips.map((trip) => (
                    <TableRow key={trip.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{trip.title}</div>
                          <div className="text-sm text-muted-foreground line-clamp-2">
                            {trip.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {trip.destination}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          {trip.price}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          {trip.currentParticipants}/{trip.maxParticipants}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{trip.startDate.toLocaleDateString()}</div>
                          <div className="text-muted-foreground">to {trip.endDate.toLocaleDateString()}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={trip.isActive ? "default" : "secondary"}>
                          {trip.isActive ? t('dashboard.admin.trips.status.active') : t('dashboard.admin.trips.status.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(trip)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(trip)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Trip Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('dashboard.admin.trips.editTrip')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.trips.editTripDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-title">{t('dashboard.admin.trips.tripTitle')}</Label>
              <Input
                id="edit-title"
                value={newTrip.title}
                onChange={(e) => setNewTrip(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('dashboard.admin.trips.titlePlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="edit-destination">{t('dashboard.admin.trips.destination')}</Label>
              <Input
                id="edit-destination"
                value={newTrip.destination}
                onChange={(e) => setNewTrip(prev => ({ ...prev, destination: e.target.value }))}
                placeholder={t('dashboard.admin.trips.destinationPlaceholder')}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="edit-description">{t('dashboard.admin.trips.description')}</Label>
              <Textarea
                id="edit-description"
                value={newTrip.description}
                onChange={(e) => setNewTrip(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('dashboard.admin.trips.descriptionPlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="edit-price">{t('dashboard.admin.trips.price')}</Label>
              <Input
                id="edit-price"
                type="number"
                value={newTrip.price}
                onChange={(e) => setNewTrip(prev => ({ ...prev, price: Number(e.target.value) }))}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="edit-duration">{t('dashboard.admin.trips.duration')}</Label>
              <Input
                id="edit-duration"
                value={newTrip.duration}
                onChange={(e) => setNewTrip(prev => ({ ...prev, duration: e.target.value }))}
                placeholder={t('dashboard.admin.trips.durationPlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="edit-maxParticipants">{t('dashboard.admin.trips.maxParticipants')}</Label>
              <Input
                id="edit-maxParticipants"
                type="number"
                value={newTrip.maxParticipants}
                onChange={(e) => setNewTrip(prev => ({ ...prev, maxParticipants: Number(e.target.value) }))}
                placeholder="0"
              />
            </div>
            <div>
              <Label htmlFor="edit-organizer">{t('dashboard.admin.trips.organizer')}</Label>
              <Input
                id="edit-organizer"
                value={newTrip.organizer}
                onChange={(e) => setNewTrip(prev => ({ ...prev, organizer: e.target.value }))}
                placeholder={t('dashboard.admin.trips.organizerPlaceholder')}
              />
            </div>
            <div>
              <Label htmlFor="edit-startDate">{t('dashboard.admin.trips.startDate')}</Label>
              <Input
                id="edit-startDate"
                type="date"
                value={newTrip.startDate}
                onChange={(e) => setNewTrip(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit-endDate">{t('dashboard.admin.trips.endDate')}</Label>
              <Input
                id="edit-endDate"
                type="date"
                value={newTrip.endDate}
                onChange={(e) => setNewTrip(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('dashboard.admin.trips.cancel')}
            </Button>
            <Button onClick={handleUpdateTrip} disabled={updating}>
              {updating ? t('dashboard.admin.trips.updating') : t('dashboard.admin.trips.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dashboard.admin.trips.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('dashboard.admin.trips.deleteDialog.description', { title: tripToDelete?.title })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('dashboard.admin.trips.deleteDialog.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? t('dashboard.admin.trips.deleteDialog.deleting') : t('dashboard.admin.trips.deleteDialog.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
